
  import {computed, defineComponent, onMounted, onUnmounted, reactive, toRefs, watch} from "vue";
  import {isAuthorized} from "@/mixin/mixins";
  import EmployeeName from "@/model/EmployeeName";
  import EmployeeService, {EmployeePerDiemV3Dto} from "@/service/EmployeeService";
  import {employeeUuid} from "@/RouterUtils";
  import SwitchView from "@/components/SwitchView.vue";
  import MonthPicker from "@/components/MonthPicker.vue";
  import {date, earliestValidFrom} from "@/store/StoreUtils";
  import moment from "moment";
  import {useRoute} from "vue-router/composables";
  import Employee from "@/model/Employee";
  import PerDiem from "@/model/PerDiem";
  import Worklog from "@/model/Worklog";
  import router from "@/router";
  import {yearMonth} from "@/DateUtils";
  import eventBus from "@/eventbus";
  import {MessageTypes} from "@/components/message-types";

  export default defineComponent({
    name: "AccountingEmployee",
    components: {
      MonthPicker,
      SwitchView
    },
    setup() {
      const initialState = {
        active: null as unknown as string,
        employee: null as unknown as EmployeeName,
        loading: false,
        perDiemsV3: new Array<EmployeePerDiemV3Dto>(),
        worklogs: new Array<Worklog>(),
        yearMonth: null as unknown as string,
        maxYearMonth: moment().format("YYYY-MM")
      };

      const state = reactive({...initialState});

      const route = useRoute();

      const employeeLoaded = EmployeeService.getEmployeeV3(employeeUuid.value, "SHALLOW")
        .then(response => {
          // The per diem and worklog endpoints take the employee number as id, so it is mapped onto the id the dialogs
          // send.
          const dto = response.data;
          const employee = new EmployeeName();
          employee.id = dto.employeeNumber;
          employee.uuid = dto.id;
          employee.employeeId = dto.employeeNumber;
          employee.firstName = dto.firstName;
          employee.lastName = dto.lastName;
          state.employee = employee;
          return state.employee;
        });

      const perDiems = computed(() => {
        return state.perDiemsV3.map(perDiem => {
          return {
            uuid: perDiem.id,
            date: perDiem.date,
            order: {uuid: perDiem.order.id, name: perDiem.order.name},
            catering: perDiem.catering,
            misc: perDiem.misc,
            breakfast: perDiem.breakfast
          } as PerDiem;
        });
      });

      const fullName = computed(() => {
        return state.employee ? state.employee.firstName + " " + state.employee.lastName : "";
      });

      const isEditable = computed(() => {
        return yearMonth(state.yearMonth).isSameOrAfter(yearMonth(earliestValidFrom.value));
      });

      watch(() => state.active, (newTab, oldTab) => {
        // oldTab can only be null in the first invocation and is triggered after mounted()
        if(oldTab == null) {
          load();
        } else if(newTab == "perdiems") {
          if (state.perDiemsV3.length == 0 || !yearMonth(state.perDiemsV3[0].date).isSame(yearMonth(state.yearMonth))) {
            // In case we jump directly into the perdiems tab without first loading any worklogs
            if(state.worklogs.length == 0) {
              load();
            } else {
              state.loading = true;
              loadPerDiems()
                .finally(() => {
                  state.loading = false;
                });
            }
          }
        }
      });

      const dateChanged = () => {
        load();

        router.push({ query: Object.assign({}, router.currentRoute.query, { yearMonth: state.yearMonth })});
      };

      const load = () => {
        state.loading = true;

        if(state.active == "worklogs") {
          loadWorklogs()
            .finally(() => {
              state.loading = false;
            });
        } else if(state.active == "perdiems") {
          Promise.all([loadWorklogs(), loadPerDiems()])
            .finally(() => {
              state.loading = false;
            });
        }
      };

      const loadPerDiems = () => {
        return employeeLoaded
          .then(employee => EmployeeService.getEmployeePerDiemsV3(employee.uuid, state.yearMonth))
          .then(perDiemsV3 => {
            state.perDiemsV3 = perDiemsV3;
          });
      };

      const loadWorklogs = () => {
        // The worklog dialog shows the employee of a worklog, which v3 does not carry
        return employeeLoaded
          .then(employee => EmployeeService.getEmployeeWorklogListV3(employee.uuid, state.yearMonth)
            .then(worklogs => {
              state.worklogs = worklogs.map(worklog => Object.assign(worklog, {employee: employee as Employee}));
            }));
      };

      onMounted(() => {
        if(route.query.yearMonth && yearMonth(route.query.yearMonth as string).isValid()) {
          state.yearMonth = route.query.yearMonth as string;
        } else {
          state.yearMonth = moment().format("YYYY-MM");
        }

        eventBus.$on(MessageTypes.PERDIEM_UPDATED, load);
        eventBus.$on(MessageTypes.WORKLOG_CREATED, load);
        eventBus.$on(MessageTypes.WORKLOG_DELETED, load);
        eventBus.$on(MessageTypes.WORKLOG_UPDATED, load);
      });

      // Only this page's own callbacks are removed, so other pages keep listening and a left page no longer reloads
      onUnmounted(() => {
        eventBus.$off(MessageTypes.PERDIEM_UPDATED, load);
        eventBus.$off(MessageTypes.WORKLOG_CREATED, load);
        eventBus.$off(MessageTypes.WORKLOG_DELETED, load);
        eventBus.$off(MessageTypes.WORKLOG_UPDATED, load);
      });

      return {
        ...toRefs(state),
        date,
        dateChanged,
        fullName,
        isAuthorized,
        isEditable,
        perDiems
      };
    }
  });
